import React from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { type FormikProps } from 'formik';
import Loading from '@components/Shared/Loader/Loading';
import useGetOnhandInventoryItemsByInventoryId from '@hooks/INV/MasterItems/useGetOnhandInventoryItemsByInventoryId';

interface LinesProps {
  formik: FormikProps<any>;

  isViewOnly?: boolean;
}

const Lines: React.FC<LinesProps> = ({ formik, isViewOnly = false }) => {
  const { values } = formik;
  const { i18n, t } = useTranslation();

  const { InventoryItemsOptions, loading, error } = useGetOnhandInventoryItemsByInventoryId({
    InventoryId: values.organizationId,
    SubInventoryId: ''
  });

  // تحديد ما إذا كان يجب منع التعديل بناءً على isConfirm أو isViewOnly
  const isDisabled = isViewOnly || values.isConfirm;

  const norm = (v: any) => (v ?? '').toString();

  const getUsedUomsForItem = (lines: any[], currentIndex: number, itemId: any) => {
    const set = new Set<string>();
    lines.forEach((l, i) => {
      if (i !== currentIndex && l?.itemId === itemId && l?.uom) set.add(norm(l.uom));
    });
    return set;
  };

  const isDuplicateItemUom = (lines: any[], currentIndex: number, itemId: any, uom: any) => {
    if (!itemId || !uom) return false;
    const u = norm(uom);
    return lines.some((l, i) => i !== currentIndex && l?.itemId === itemId && norm(l?.uom) === u);
  };

  const handleLineChange = (index: number, field: string, value: any) => {
    // إذا كان مؤكداً، لا تسمح بإجراء أي تغييرات
    if (values.isConfirm) {
      return;
    }

    // إنشاء نسخة جديدة من المصفوفة لتجنب التعديل المباشر على القيم
    const updatedLines = [...formik.values.equipmentsWorkOrderItemList];

    // تحديث الحقل المحدد في السطر المحدد
    updatedLines[index] = {
      ...updatedLines[index],
      [field]: value
    };

    // // إذا كان الحقل الذي تم تغييره هو itemId، نريد تحديث UOM تلقائياً إذا كان متاحاً
    // if (field === 'itemId' && value) {
    //   const selectedItem = InventoryItemsOptions?.find(item => item.value === value);
    //   if (selectedItem) {
    //     updatedLines[index] = {
    //       ...updatedLines[index],
    //       uom: selectedItem.uom || null,
    //       // unitCost: selectedItem.purchasePrice || 0
    //       unitCost: 0
    //     };
    //   }
    // }

    if (field === 'itemId') {
      // عند تغيير الصنف: حدّث الصنف + صفّر/اضبط الوحدة بطريقة تمنع التكرار
      const newItemId = value ?? null;

      const usedUoms = getUsedUomsForItem(updatedLines, index, newItemId);
      const selectedItem = InventoryItemsOptions?.find(item => item.value === newItemId);

      const defaultUom = selectedItem?.uom ?? null;

      updatedLines[index] = {
        ...updatedLines[index],
        itemId: newItemId,
        // اختر defaultUom فقط إذا غير مستخدم، وإلا خلّها null ليختار المستخدم وحدة أخرى
        uom: defaultUom && !usedUoms.has(norm(defaultUom)) ? defaultUom : null,
        unitCost: 0
      };

      // امسح أي خطأ سابق على uom
      formik.setFieldError(`equipmentsWorkOrderItemList[${index}].uom`, undefined as any);
    }

    if (field === 'uom') {
      const newUom = value ?? null;
      const itemId = updatedLines[index]?.itemId;

      // لو تكرار (نفس item + نفس uom) -> امنعه
      if (isDuplicateItemUom(updatedLines, index, itemId, newUom)) {
        updatedLines[index] = { ...updatedLines[index], uom: null };
        formik.setFieldError(
          `equipmentsWorkOrderItemList[${index}].uom`,
          t('duplicateItemUom') // أضفها في الترجمة
        );
      } else {
        updatedLines[index] = { ...updatedLines[index], uom: newUom };
        formik.setFieldError(`equipmentsWorkOrderItemList[${index}].uom`, undefined as any);
      }
    }

    // إذا كان الحقل الذي تم تغييره هو الكمية أو سعر الوحدة، نقوم بحساب المجموع تلقائياً
    if (field === 'qty' || field === 'unitCost') {
      const qty = field === 'qty' ? Number(value) : Number(updatedLines[index].qty);
      const unitCost = field === 'unitCost' ? Number(value) : Number(updatedLines[index].unitCost);

      updatedLines[index] = {
        ...updatedLines[index],
        totalCost: qty * unitCost
      };
    }

    // تحديث القيم في formik
    formik.setFieldValue('equipmentsWorkOrderItemList', updatedLines);
  };

  const handleAddLine = () => {
    // إذا كان مؤكداً، لا تسمح بإضافة سطور جديدة
    if (values.isConfirm) {
      return;
    }

    const newLine: any = {
      id: null,
      itemId: null,
      uom: null,
      qty: 0,
      unitCost: 0,
      totalCost: 0,
      notes: ''
    };
    formik.setFieldValue('equipmentsWorkOrderItemList', [...formik.values.equipmentsWorkOrderItemList, newLine]);
  };

  const handleDeleteLine = (index: number) => {
    // إذا كان مؤكداً، لا تسمح بحذف السطور
    if (values.isConfirm) {
      return;
    }

    const updatedLines = values.equipmentsWorkOrderItemList?.filter((_: any, i: number) => i !== index);
    formik?.setFieldValue('equipmentsWorkOrderItemList', updatedLines);
  };

  // console.log(InventoryItemsOptions);

  return (
    <>
      <Loading loading={loading} error={error} Type='Dots'>
        {!isViewOnly && (
          <div className=' d-flex justify-content-between bg-theme bg-opacity-15 mb-2'>
            <h5 className={`fw-bold border-start  border-4 border-theme p-2 m-0 d-flex align-items-center`}>
              <i className='fas fa-cogs fa-fw me-2 '></i> {t('SpareParts')}
            </h5>

            <button type='button' className='btn btn-sm btn-theme' onClick={handleAddLine} hidden={isDisabled}>
              <i className='fas fa-tools me-2 fa-lg'></i> {t('Add')}
            </button>
          </div>
        )}

        {/* عرض رسالة إذا كان الطلب مؤكداً */}
        {values.isConfirm && (
          <div className='alert alert-warning text-center'>
            <i className='fas fa-lock me-2'></i>
            {t('orderConfirmedNoEdit')}
          </div>
        )}

        {values.equipmentsWorkOrderItemList?.length === 0 && (
          <div className='alert alert-info text-center'>
            <i className='fas fa-exclamation-triangle me-2'></i>
            {t('noPartsAdded')}
          </div>
        )}

        {values.equipmentsWorkOrderItemList?.length > 0 && (
          <div className=''>
            <div
              className={`table-responsive rounded-2 text-center ${
                formik.errors.equipmentsWorkOrderItemList ? ' ' : ''
              } `}
            >
              <div style={{ width: '100%' }}>
                <table className='table table-borderless  table-hover mb-0 '>
                  <thead className=' ' style={{ backgroundColor: 'var(--bs-tertiary-bg)' }}>
                    <tr>
                      <th className='align-middle' style={{ width: '2%' }}>
                        #
                      </th>
                      <th className='align-middle' style={{ width: '30%' }}>
                        {t('itemName')}
                      </th>
                      <th className='align-middle' style={{ width: '10%' }}>
                        {t('unit')}
                      </th>
                      <th className='align-middle' style={{ width: '5%' }}>
                        {t('quantity')}
                      </th>
                      <th className='align-middle' style={{ width: '5%' }}>
                        {t('price')}
                      </th>

                      <th className='align-middle' style={{ width: '5%' }}>
                        {t('total')}
                      </th>
                      <th className='align-middle' style={{ width: '18%' }}>
                        {t('description')}
                      </th>
                      <th
                        hidden={isViewOnly || values.isConfirm}
                        className='align-middle text-end'
                        style={{ width: '2%' }}
                      ></th>
                    </tr>
                  </thead>
                  <tbody className='fw-bold'>
                    {values.equipmentsWorkOrderItemList
                      ?.slice()
                      .reverse()
                      ?.map((line: any, index: any) => {
                        const Index = values.equipmentsWorkOrderItemList?.length - 1 - index;

                        const usedUoms = getUsedUomsForItem(values.equipmentsWorkOrderItemList, Index, line.itemId);

                        // const selectedItems = values.equipmentsWorkOrderItemList
                        //   ?.filter((_: any, idx: any) => idx !== Index) // استبعاد العنصر الحالي
                        //   ?.map((line: any) => line.ItemId)
                        //   ?.filter(Boolean); // إزالة القيم الفارغة

                        const selectedItemIds = new Set(
                          (values.equipmentsWorkOrderItemList ?? [])
                            .filter((_: any, idx: any) => idx !== Index)
                            .map((l: any) => l?.itemId)
                            .filter(Boolean)
                        );

                        return (
                          <tr key={index}>
                            <td className='align-middle  small' style={{ backgroundColor: 'var(--bs-tertiary-bg)' }}>
                              {values.equipmentsWorkOrderItemList?.length - index}
                            </td>
                            <td className='align-middle small'>
                              <Select
                                isDisabled={isDisabled}
                                classNamePrefix='react-select'
                                className='w-100'
                                isSearchable
                                isClearable
                                // options={InventoryItemsOptions?.filter(
                                //   (item, index, self) =>
                                //     index === self?.findIndex(i => i.itemId === item.itemId) &&
                                //     !selectedItems.includes(item.itemId) // استبعاد العناصر المختارة مسبقًا
                                // )}

                                options={InventoryItemsOptions?.filter(
                                  (item, idx, self) => idx === self.findIndex(i => i.itemId === item.itemId)
                                )}

                                // ✅ فلترة: الصنف المضاف لا يظهر في الخيارات (منع تكرار الصنف نهائياً)
                                // options={InventoryItemsOptions?.filter((item: any, idx: any, self: any[]) => {
                                //   const isUnique = idx === self.findIndex(i => i.itemId === item.itemId);
                                //   const isCurrentLineItem = item.itemId === line.itemId; // خليه يظهر للسطر الحالي حتى لا يختفي الاختيار
                                //   const isAlreadySelectedElsewhere = selectedItemIds.has(item.itemId);
                                //   return isUnique && (isCurrentLineItem || !isAlreadySelectedElsewhere);
                                // })}
                                onChange={option =>
                                  handleLineChange(Index, 'itemId', option === null ? null : option.value)
                                }
                                value={
                                  line.itemId === null
                                    ? null
                                    : InventoryItemsOptions?.find(option => option.value === line.itemId)
                                }
                                placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                                }}
                                menuPlacement='auto'
                                formatOptionLabel={option => (
                                  <div className='d-flex justify-content-between   '>
                                    <div className=''>{option.label}</div>
                                    <div className=' text-muted fs-12px '>{option.itemCode}</div>
                                  </div>
                                )}
                                filterOption={({ label, data }, input) => {
                                  const searchValue = input?.toLowerCase();
                                  return (
                                    label?.toLowerCase().includes(searchValue) ||
                                    data.itemCode?.toLowerCase().includes(searchValue)
                                  );
                                }}
                              />
                            </td>

                            <td className='align-middle small'>
                              <select
                                disabled={isDisabled}
                                className='form-select text-center'
                                value={line.uom || ''}
                                onChange={e => handleLineChange(Index, 'uom', e.target.value)}
                              >
                                <option value=''>{i18n.language === 'ar' ? 'إختر' : 'Choose'}</option>
                                {/* {InventoryItemsOptions?.filter(f => f.itemId === line.itemId)?.map((item, idx) => (
                                  <option key={++idx} value={item.uom}>
                                    {item.uomName}
                                  </option>
                                ))} */}
                                {InventoryItemsOptions?.filter(
                                  f => f.itemId === line.itemId && !usedUoms.has(norm(f.uom))
                                )?.map((item, idx) => (
                                  <option key={++idx} value={item.uom}>
                                    {item.uomName}
                                  </option>
                                ))}
                              </select>
                            </td>

                            <td className='align-middle small'>
                              <input
                                disabled={isDisabled}
                                type='text'
                                autoComplete='off'
                                className='form-control text-center px-0'
                                value={line.qty}
                                onChange={e => handleLineChange(Index, 'qty', Number(e.target.value))}
                                onInput={e => {
                                  // إلغاء أي إدخال غير رقمي يدويًا
                                  const input = e.target as HTMLInputElement;
                                  input.value = input.value.replace(/[^0-9]/g, '');
                                }}
                              />
                            </td>

                            <td className='align-middle small'>
                              <input
                                disabled
                                type='text'
                                autoComplete='off'
                                className='form-control text-center px-0'
                                value={line.unitCost}
                                onChange={e => handleLineChange(Index, 'unitCost', Number(e.target.value))}
                                onInput={e => {
                                  const input = e.target as HTMLInputElement;
                                  input.value = input.value.replace(/[^0-9]/g, '');
                                }}
                              />
                            </td>

                            <td className='align-middle small'>{line.unitCost * line.qty}</td>
                            <td className='align-middle small'>
                              <input
                                disabled={isDisabled}
                                className='form-control'
                                value={line.notes}
                                onChange={e => handleLineChange(Index, 'notes', e.target.value)}
                              />
                            </td>
                            <td className='align-middle small' hidden={isDisabled}>
                              <button
                                type='button'
                                className='btn btn-outline-danger'
                                onClick={() => handleDeleteLine(Index)}
                              >
                                <i className='fas fa-trash-alt'></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </Loading>
    </>
  );
};

export default Lines;
